import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { templateAPI, healthRecordsAPI } from '../services/api';
import TemplateDetailModal from '../Modal/TemplateDetailModal';

const AutocompleteWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #D1D5DB;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.div`
  padding: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F3F4F6;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }
`;

const EditTemplateModal = ({ isOpen, onClose, recordId, selectedRecord, onSave }) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [departmentFrom, setDepartmentFrom] = useState('เลือก');
  const [forGroup, setForGroup] = useState('เลือก');
  const [tagInput, setTagInput] = useState('');
  const [selectedSearchTags, setSelectedSearchTags] = useState([]); // tags ที่พิมพ์เพิ่มไว้ค้นหา
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateSelections, setTemplateSelections] = useState({});
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [filteredTagSuggestions, setFilteredTagSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ดึงข้อมูล templates, tags และ hidden templates จาก API
  useEffect(() => {
    if (!isOpen || !recordId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        // ดึง templates ทั้งหมด และกรองเฉพาะที่ active
        const templatesResponse = await templateAPI.getAll();
        let activeTemplates = [];

        if (templatesResponse.status === 'success' && templatesResponse.data) {
          // กรองเฉพาะ template ที่ is_active = true หรือ 1 (ไม่แสดง template ที่ถูก deactivate)
          activeTemplates = templatesResponse.data.filter(template =>
            template.is_active === true || template.is_active === 1
          );
          setAllTemplates(activeTemplates);
          setFilteredTemplates(activeTemplates); // เริ่มต้นแสดงทั้งหมด

          // รวม tags ทั้งหมดจาก templates
          const allTags = activeTemplates.reduce((acc, template) => {
            if (template.tags && Array.isArray(template.tags)) {
              template.tags.forEach(tag => {
                if (tag && !acc.includes(tag)) {
                  acc.push(tag);
                }
              });
            }
            return acc;
          }, []);
          setAvailableTags(allTags);
        }

        // 🔥 ดึงข้อมูล health record ล่าสุดจาก DB (ไม่ใช้ selectedRecord ที่อาจเก่า)
        const healthRecordResponse = await healthRecordsAPI.getById(recordId);
        let currentlyShownTemplateIds = [];
        let latestTags = [];

        if (healthRecordResponse.status === 'success' && healthRecordResponse.data) {
          const healthData = healthRecordResponse.data.health_data;

          // ดึง matched templates ล่าสุด
          if (healthData?.matched_templates && Array.isArray(healthData.matched_templates)) {
            currentlyShownTemplateIds = healthData.matched_templates.map(t => t.template_id);
          }

          // ดึง tags ล่าสุดจาก DB
          if (healthData?.tags && Array.isArray(healthData.tags)) {
            latestTags = healthData.tags;
          }
        }

        // ตั้งค่า checkbox selections ตามข้อมูลล่าสุดจาก DB
        const initialSelections = {};
        activeTemplates.forEach(template => {
          initialSelections[template.id] = currentlyShownTemplateIds.includes(template.id);
        });
        setTemplateSelections(initialSelections);

        // ใช้ tags ล่าสุดจาก DB
        setSelectedFilters(latestTags);
      } catch (error) {
        console.error('✗ Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, recordId]);


  const removeFilter = (filterToRemove) => {
    setSelectedFilters(prev => prev.filter(filter => filter !== filterToRemove));
  };

  const handleCheckboxChange = async (templateId, checked) => {
    // 1. อัพเดท UI ทันที (Optimistic Update)
    const updatedSelections = {
      ...templateSelections,
      [templateId]: checked
    };
    setTemplateSelections(updatedSelections);

    // 2. บันทึกลง DB พร้อมกับอัพเดท tags
    try {
      const selectedTemplateIds = [];
      for (const [id, isSelected] of Object.entries(updatedSelections)) {
        if (isSelected) {
          selectedTemplateIds.push(parseInt(id));
        }
      }

      // บันทึกลง DB
      await healthRecordsAPI.saveManualTemplates(recordId, selectedTemplateIds);

      // 3. ดึง tags ใหม่จาก DB (เพราะ tags เปลี่ยนตาม templates)
      await reloadTagsFromDB();
    } catch (error) {
      console.error('❌ Error auto-saving templates:', error);
      // Rollback UI ถ้า save ไม่สำเร็จ
      await reloadDataFromDB();
    }
  };

  // ฟังก์ชันดึง tags ล่าสุดจาก DB (ไม่แตะ checkbox selections)
  const reloadTagsFromDB = async () => {
    try {
      const healthRecordResponse = await healthRecordsAPI.getById(recordId);

      if (healthRecordResponse.status === 'success' && healthRecordResponse.data) {
        const healthData = healthRecordResponse.data.health_data;

        // อัพเดท tags เท่านั้น
        if (healthData?.tags && Array.isArray(healthData.tags)) {
          setSelectedFilters(healthData.tags);
        } else {
          setSelectedFilters([]);
        }
      }
    } catch (error) {
      console.error('Error reloading tags from DB:', error);
    }
  };

  // ฟังก์ชันดึงข้อมูลทั้งหมดจาก DB (templates และ tags) - ใช้เมื่อต้อง rollback
  const reloadDataFromDB = async () => {
    try {
      // ดึงข้อมูล health record ล่าสุดจาก DB
      const healthRecordResponse = await healthRecordsAPI.getById(recordId);

      if (healthRecordResponse.status === 'success' && healthRecordResponse.data) {
        const healthData = healthRecordResponse.data.health_data;

        // อัพเดท matched templates
        let currentlyShownTemplateIds = [];
        if (healthData?.matched_templates && Array.isArray(healthData.matched_templates)) {
          currentlyShownTemplateIds = healthData.matched_templates.map(t => t.template_id);
        }

        // อัพเดท checkbox selections
        const updatedSelections = {};
        allTemplates.forEach(template => {
          updatedSelections[template.id] = currentlyShownTemplateIds.includes(template.id);
        });
        setTemplateSelections(updatedSelections);

        // อัพเดท tags
        if (healthData?.tags && Array.isArray(healthData.tags)) {
          setSelectedFilters(healthData.tags);
        }
      }
    } catch (error) {
      console.error('❌ Error reloading data from DB:', error);
    }
  };

  const handleTagClick = async (filterName) => {
    // หา template ที่ตรงกับ filter ที่คลิก
    const templateToShow = allTemplates.find(template =>
      template.template_name.includes(filterName) ||
      template.tags?.some(tag => tag.includes(filterName))
    );

    if (templateToShow) {
      try {
        // ดึงข้อมูล template แบบเต็มจาก API
        const response = await templateAPI.getById(templateToShow.id);
        if (response.status === 'success' && response.data) {
          const template = response.data;

          // แปลงข้อมูลให้เข้ากับ format ของ TemplateDetailModal
          const formattedData = {
            templateName: template.template_name,
            createdBy: template.created_by,
            tags: template.tags || [],
            gender: template.gender === 'male' ? 'ชาย' : template.gender === 'female' ? 'หญิง' : 'ทั้งสองเพศ',
            ageRange: `${template.age_min}-${template.age_max}`,
            parameters: template.parameters?.map(p => p.parameter_name) || [],
            normalValues: template.parameters?.map(p => p.normal_range_text) || [],
            setValues: template.parameters?.map(p => `${p.custom_min}-${p.custom_max}`) || []
          };

          setSelectedTemplateData(formattedData);
          setIsDetailModalOpen(true);
        }
      } catch (error) {
        console.error('Error loading template details:', error);
      }
    }
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedTemplateData(null);
  };

  const handleSave = async () => {
    if (!recordId) return;

    setLoading(true);
    try {
      // ไม่ต้องบันทึกอีก เพราะบันทึกแล้วตอนติ้ก checkbox
      // แค่รีเฟรชข้อมูลหน้าหลัก
      if (onSave) {
        await onSave();
      }

      onClose();
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSearch = () => {
    // กรอง templates ตามเงื่อนไข
    let filtered = allTemplates;

    // ค้นหาตามชื่อ Template
    if (templateName.trim()) {
      filtered = filtered.filter(template =>
        template.template_name.toLowerCase().includes(templateName.toLowerCase())
      );
    }

    // ค้นหาตามผู้สร้าง
    if (departmentFrom && departmentFrom !== 'เลือก') {
      filtered = filtered.filter(template =>
        template.created_by && template.created_by.includes(departmentFrom)
      );
    }

    // กรองตามเพศ
    if (forGroup && forGroup !== 'เลือก') {
      const genderMap = {
        'male': 'male',
        'female': 'female',
        'both': 'both'
      };
      filtered = filtered.filter(template =>
        template.gender === genderMap[forGroup] || template.gender === 'both'
      );
    }

    // กรองตาม tags ที่เลือก
    if (selectedSearchTags.length > 0) {
      filtered = filtered.filter(template =>
        selectedSearchTags.some(selectedTag =>
          template.tags && template.tags.includes(selectedTag)
        )
      );
    }

    setFilteredTemplates(filtered);
  };

  const removeSearchTag = (tagToRemove) => {
    setSelectedSearchTags(selectedSearchTags.filter(tag => tag !== tagToRemove));
  };

  const addCustomTag = () => {
    if (tagInput.trim() && !selectedSearchTags.includes(tagInput.trim())) {
      setSelectedSearchTags([...selectedSearchTags, tagInput.trim()]);
      setTagInput("");
      setShowSuggestions(false);
    }
  };

  const handleTagInputChange = (value) => {
    setTagInput(value);

    if (value.trim()) {
      const filtered = availableTags.filter(tag =>
        tag.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredTagSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (tag) => {
    if (!selectedSearchTags.includes(tag)) {
      setSelectedSearchTags([...selectedSearchTags, tag]);
    }
    setTagInput("");
    setShowSuggestions(false);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            fontSize: '25px',
            fontWeight: '400',
            margin: 0,
            color: '#000000'
          }}>
            แก้ไข Template ผลสุขภาพ
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#6B7280'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '32px' }}>
          {/* การแปลผลสุขภาพ section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '500',
              marginBottom: '16px',
              textAlign: 'center',
              color: '#171717'
            }}>
              การแปลผลสุขภาพ
            </h3>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              {allTemplates
                .filter(template => templateSelections[template.id] === true)
                .map((template, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: 'transparent',
                      border: '1px solid #00B5B8',
                      borderRadius: '0px',
                      padding: '4px 8px',
                      fontSize: '14px',
                      color: '#00B5B8',
                      position: 'relative',
                      width: 'fit-content'
                    }}
                  >
                    <span>{template.template_name}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Form section - แบบเดียวกับ template.js */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr auto auto',
            gridTemplateRows: 'auto auto auto',
            gap: '16px',
            alignItems: 'end',
            marginBottom: '16px'
          }}>
            {/* แถวที่ 1 - Headers */}
            <div style={{ gridColumn: '1', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                ค้นหาชื่อ Template
              </label>
            </div>
            <div style={{ gridColumn: '2', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                ค้นหาจากผู้สร้าง Template
              </label>
            </div>
            <div style={{ gridColumn: '3', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                สำหรับเพศ
              </label>
            </div>
            <div style={{ gridColumn: '4', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                ป้ายกำกับ Tag
              </label>
            </div>
            
            {/* แถวที่ 2 - Input Fields */}
            <div style={{ gridColumn: '1', gridRow: '2' }}>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="พิมพ์ชื่อค้นหา"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #CFD3D4',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ gridColumn: '2', gridRow: '2' }}>
              <select
                value={departmentFrom}
                onChange={(e) => setDepartmentFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">เลือก</option>
                {[...new Set(allTemplates.map(t => t.created_by).filter(Boolean))].map((creator, index) => (
                  <option key={index} value={creator}>{creator}</option>
                ))}
              </select>
            </div>
            
            <div style={{ gridColumn: '3', gridRow: '2' }}>
              <select
                value={forGroup}
                onChange={(e) => setForGroup(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">เลือก</option>
                <option value="male">เพศชาย</option>
                <option value="female">เพศหญิง</option>
                <option value="both">ทั้งสองเพศ</option>
              </select>
            </div>
            
            <div style={{ gridColumn: '4', gridRow: '2' }}>
              <AutocompleteWrapper>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => handleTagInputChange(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addCustomTag();
                    }
                  }}
                  placeholder="พิมพ์ tag ใหม่"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #CFD3D4',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {showSuggestions && filteredTagSuggestions.length > 0 && (
                  <SuggestionsDropdown>
                    {filteredTagSuggestions.map((tag, index) => (
                      <SuggestionItem
                        key={index}
                        onClick={() => selectSuggestion(tag)}
                      >
                        {tag}
                      </SuggestionItem>
                    ))}
                  </SuggestionsDropdown>
                )}
              </AutocompleteWrapper>
            </div>

            <div style={{ gridColumn: '5', gridRow: '2' }}>
              <button
                onClick={addCustomTag}
                style={{
                  backgroundColor: '#FBFFFF',
                  color: '#9c9e9eff',
                  border: '1px solid #CFD3D4',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  height: 'fit-content'
                }}>
                เพิ่ม
              </button>
            </div>
            
            <div style={{ gridColumn: '6', gridRow: '2' }}>
              <button
                onClick={handleSearch}
                style={{
                  backgroundColor: '#00B5B8',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  height: 'fit-content'
                }}>
                ค้นหา
              </button>
            </div>

            {/* แถวที่ 3 - Tags ที่พิมพ์เพิ่มไว้ค้นหา */}
            <div style={{ gridColumn: '4', gridRow: '3' }}>
              {selectedSearchTags.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    fontSize: '14px',
                    flexWrap: 'wrap'
                  }}>
                    {selectedSearchTags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid #00B5B8',
                          borderRadius: '0px',
                          padding: '4px 8px',
                          color: '#00B5B8',
                          position: 'relative',
                          width: 'fit-content'
                        }}
                      >
                        {tag}
                        <button
                          onClick={() => removeSearchTag(tag)}
                          style={{
                            background: 'white',
                            border: '1px solid #00B5B8',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            color: '#00B5B8'
                          }}
                        >
                          <X size={8} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>


          <hr style={{ border: '1px solid #E5E7EB', margin: '24px 0' }} />

          {/* Table - แบบเดียวกับ template.js */}
          <div style={{ 
            overflowX: 'auto',
            border: '1px solid #E5E7EB',
            borderRadius: '8px'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead style={{ backgroundColor: '#F9FAFB' }}>
                <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ที่</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>Template</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>สร้างโดย</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ป้ายกำกับ</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>สำหรับเพศ</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ช่วงอายุ (ปี)</th>
                  <th colSpan="3" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'center'
                  }}>ผลตรวจสุขภาพ</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'center'
                  }}>เลือก</th>
                </tr>
                <tr>
                  <th style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>พารามิเตอร์</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ค่าปกติ</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ค่าที่กำหนด</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                      กำลังโหลดข้อมูล...
                    </td>
                  </tr>
                ) : filteredTemplates.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                      ไม่พบ Templates ที่ตรงกับเงื่อนไขการค้นหา
                    </td>
                  </tr>
                ) : (
                  filteredTemplates.map((template, index) => (
                    <tr key={template.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top',
                        fontWeight: '500'
                      }}>{index + 1}</td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>{template.template_name}</td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>{template.created_by || '-'}</td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>
                        {template.tags && template.tags.length > 0 ? (
                          template.tags.map((tag, tagIndex) => (
                            <div key={tagIndex}>• {tag}</div>
                          ))
                        ) : '-'}
                      </td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>
                        {template.gender === 'male' ? 'ชาย' : template.gender === 'female' ? 'หญิง' : 'ทั้งสองเพศ'}
                      </td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>{template.age_min}-{template.age_max}</td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>
                        {template.parameters?.map((param, paramIndex) => (
                          <div key={paramIndex}>{param.parameter_name}</div>
                        )) || '-'}
                      </td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>
                        {template.parameters?.map((param, paramIndex) => (
                          <div key={paramIndex}>{param.normal_range_text || '-'}</div>
                        )) || '-'}
                      </td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top'
                      }}>
                        {template.parameters?.map((param, paramIndex) => (
                          <div key={paramIndex}>{param.custom_min}-{param.custom_max}</div>
                        )) || '-'}
                      </td>
                      <td style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: '#374151',
                        verticalAlign: 'top',
                        textAlign: 'center'
                      }}>
                        <input
                          type="checkbox"
                          checked={templateSelections[template.id] || false}
                          onChange={(e) => handleCheckboxChange(template.id, e.target.checked)}
                          style={{
                            width: '16px',
                            height: '16px',
                            accentColor: '#00B5B8'
                          }}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '32px'
          }}>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                padding: '8px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              ยกเลิก
            </button>
            <button
              onClick={handleSave}
              style={{
                backgroundColor: '#00B5B8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              บันทึก
            </button>
          </div>
        </div>
      </div>

      {/* TemplateDetailModal ซ้อนทับ - z-index สูงกว่า */}
      {isDetailModalOpen && (
        <div style={{ zIndex: 1100 }}>
          <TemplateDetailModal
            isOpen={isDetailModalOpen}
            onClose={handleDetailModalClose}
            templateData={selectedTemplateData}
          />
        </div>
      )}
    </div>
  );
};

export default EditTemplateModal;